import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { TabButton } from 'components/ui/Tabs/TabButton';
import {
  useIsAdmin,
  useIsFullWidth,
  useIsLoggedIn,
  useIsMasterAccount,
  useProfile,
} from 'hooks';
import { MainLayout } from 'layouts/MainLayout';
import Head from 'next/head';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Routes } from 'services/routes';
import { useRouter } from 'next/router';

export interface EventsPageLayoutProps {
  children?: ReactNode;
  title?: string;
  active: TabButtonType.Tickets | TabButtonType.Saved | 'hosted';
}

export enum TabButtonType {
  Tickets = 'tickets',
  Saved = 'saved',
}

export const EventsPageLayout = ({
  children,
  title = 'CampusLush | Tickets',
  active = TabButtonType.Tickets,
}: EventsPageLayoutProps): ReactElement => {
  const { loading } = useProfile();
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();

  const isAdmin = useIsAdmin();
  const isMasterAccount = useIsMasterAccount();

  const isFullWidth = useIsFullWidth();

  const eventsContainerRef = useRef<HTMLDivElement | null>(null);

  const handleTabChange = (
    tab: TabButtonType.Saved | TabButtonType.Tickets,
  ) => {
    router.push(
      tab === TabButtonType.Tickets ? Routes.eventTickets() : Routes.myEvents(),
    );
  };

  useEffect(() => {
    setTimeout(() => {
      const container = eventsContainerRef.current;
      if (container) {
        const activeElement = container.querySelector(`#${active}`)
          ?.parentElement as HTMLElement;
        if (activeElement) {
          const containerCenter = container.offsetWidth;
          const elementCenter =
            activeElement.offsetLeft + activeElement.offsetWidth;
          container.scrollLeft = elementCenter - containerCenter;
        }
      }
    }, 1);
  }, [active]);

  const navContent = useMemo(
    () => (
      <Box
        height="100%"
        width="100%"
        borderRight={isFullWidth ? '1px solid #dedede' : 'none'}
      >
        {isFullWidth && (
          <Typography
            variant="h2"
            color="primary"
            sx={{
              fontSize: pxToEm(22),
              fontWeight: 500,
            }}
          >
            Check In
          </Typography>
        )}
        {!loading && (
          <Box
            mt={24}
            component="nav"
            role="navigation"
            p={0}
            display="flex"
            flexDirection="row"
            gap={2}
            alignItems="center"
          >
            <TabButton
              label="My Tickets"
              value={TabButtonType.Tickets}
              selected={active === TabButtonType.Tickets}
              onClick={() => handleTabChange(TabButtonType.Tickets)}
            />
            <TabButton
              label="Saved Events"
              value={TabButtonType.Saved}
              selected={active === TabButtonType.Saved}
              onClick={() => handleTabChange(TabButtonType.Saved)}
            />
          </Box>
        )}
        {!loading && (
          <Typography
            variant="h3"
            sx={{
              fontSize: pxToEm(18),
              fontWeight: 500,
              mt: 32,
              textAlign: 'left',
            }}
          >
            {active === TabButtonType.Tickets ? 'My Tickets' : 'Saved Events'}
          </Typography>
        )}
      </Box>
    ),
    [isAdmin, isMasterAccount, isLoggedIn, loading, isFullWidth, active],
  );

  return (
    <MainLayout>
      <Head>
        <title>{title}</title>
      </Head>

      {isFullWidth && <Box>{navContent}</Box>}

      {!isFullWidth && (
        <Box mt={30} mb={30}>
          <Typography
            variant="h1"
            sx={{
              fontSize: pxToEm(24),
              fontWeight: 500,
              marginBottom: 16,
              lineHeight: '36px',
              color: '#000',
            }}
            color="primary"
          >
            Check In
          </Typography>
          <Box
            ref={eventsContainerRef}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <TabButton
              label="My Tickets"
              value={TabButtonType.Tickets}
              selected={active === TabButtonType.Tickets}
              onClick={() => handleTabChange(TabButtonType.Tickets)}
            />
            <TabButton
              label="Saved Events"
              value={TabButtonType.Saved}
              selected={active === TabButtonType.Saved}
              onClick={() => handleTabChange(TabButtonType.Saved)}
            />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: pxToEm(18),
              fontWeight: 500,
              mt: 32,
              textAlign: 'left',
            }}
          >
            {active === TabButtonType.Tickets ? 'My Tickets' : 'Saved Events'}
          </Typography>
        </Box>
      )}

      {children}
    </MainLayout>
  );
};
